/**
 * @file DouleurCervicaleSport.tsx
 * @summary Article de blog — DouleurCervicaleSport
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, QuandConsulter, AdaptationChistera, AdaptationTable, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/douleur-cervicale-sport";
const TITLE = "Douleur cervicale et sport : mobilité, posture, charge — ce qu'on peut faire";
const DESC = "Cervicalgie, raideur de nuque, tension trapèzes : comprendre l'origine et adapter sa pratique CrossFit. Approche posture + mobilité + renforcement par CrossFit Chistera.";

const author = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };
const reviewer = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };

const faq = [
  { q: "Le CrossFit aggrave-t-il les cervicalgies ?", a: "S'il est mal adapté, oui (overhead lourd, kipping mal exécuté, posture en hyperextension). Bien dosé et avec un travail de mobilité thoracique, il améliore très souvent les douleurs cervicales chroniques liées à une posture sédentaire." },
  { q: "J'ai mal à la nuque après les séances overhead. Pourquoi ?", a: "Souvent un manque de mobilité thoracique qui force les cervicales à compenser en hyperextension. Travailler le rachis thoracique (foam roller, thoracic extensions) débloque la situation dans 80 % des cas." },
  { q: "Les wall balls me déclenchent une douleur de nuque. Que faire ?", a: "Vérifier le placement de la tête : beaucoup de débutants regardent la cible en hyperextension marquée. Garder un menton légèrement rentré, regard à hauteur d'œil, et la cervicale ne souffrira plus." },
  { q: "Une hernie cervicale interdit-elle le CrossFit ?", a: "Pas systématiquement. Une hernie asymptomatique (très fréquente après 40 ans) ne contre-indique rien. Une hernie symptomatique avec irradiation dans le bras nécessite un avis spécialisé avant de poursuivre overhead lourd et kipping." },
  { q: "Faut-il porter une minerve quand on a mal à la nuque ?", a: "Sauf prescription médicale (post-traumatisme), non. La minerve immobilise et fragilise. La mobilité douce et le renforcement adapté sont bien plus efficaces." },
  { q: "Quelle place pour le travail des trapèzes ?", a: "Très importante. Les trapèzes (les trois faisceaux : supérieur, moyen, inférieur) stabilisent la scapula, qui elle-même conditionne la mobilité cervicale. Face pulls, Y-T-W, rowings, scapular pull-ups : tout cela aide." },
];

const adaptations = [
  { standard: "Overhead press lourd", adapted: "Strict press charge modérée, prise rapprochée" },
  { standard: "Push press / push jerk explosif", adapted: "Strict press avec pause" },
  { standard: "Snatch / OHS", adapted: "Front squat + strict press séparés" },
  { standard: "Wall balls", adapted: "Front squat léger + lancers à hauteur de poitrine" },
  { standard: "Kipping pull-ups", adapted: "Strict pull-ups ou ring rows" },
  { standard: "Toes-to-bar", adapted: "Hanging knee raises" },
  { standard: "Burpees rapides", adapted: "Burpees step-down avec contrôle de la tête" },
  { standard: "Handstand push-ups", adapted: "Pike push-ups inclinées" },
];

export default function DouleurCervicaleSport() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Douleur cervicale et sport : mobilité, posture, charge"
      breadcrumbLabel="Douleur cervicale"
      category="Sport santé · Douleur ciblée"
      author={author} reviewer={reviewer}
      publishedAt="24 novembre 2026" updatedAt="24 novembre 2026" readingMinutes={8}
      ctaVariant="douleur" needsHealthDisclaimer
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-11-24", author, reviewer, faq })}
    >
      <EnBref>
        <p>
          La cervicalgie est l'une des douleurs les plus répandues chez l'adulte sédentaire. Le sport bien mené n'est pas un facteur aggravant — c'est même <strong>l'un des meilleurs traitements</strong> des cervicalgies chroniques liées à la posture (8h/jour devant un écran).
        </p>
        <p>
          La clé : travailler la <strong>mobilité thoracique</strong>, soulager les <strong>trapèzes supérieurs</strong> en les répartissant, renforcer les <strong>fléchisseurs profonds du cou</strong> et adapter les mouvements overhead le temps que la situation se stabilise.
        </p>
      </EnBref>

      <h2>D'où vient la douleur cervicale chez le sportif</h2>
      <p><strong>La posture quotidienne.</strong> 8 heures par jour, tête en avant sur un écran, épaules enroulées. Les fléchisseurs profonds du cou s'inhibent, les trapèzes supérieurs prennent tout le travail, le rachis thoracique se raidit. Vous arrivez en cours dans cet état — et un push press lourd suffit à déclencher une crise.</p>
      <p><strong>Le manque de mobilité thoracique.</strong> Quand le haut du dos ne tourne pas, ce sont les cervicales qui compensent en hyperextension dans tous les mouvements overhead. Travailler le thoracique change tout.</p>
      <p><strong>Le placement de la tête en mouvement.</strong> Beaucoup de débutants regardent la cible en wall ball avec hyperextension cervicale marquée. Sur 50 répétitions, c'est garanti pour finir avec une raideur de nuque.</p>
      <p><strong>Le kipping mal exécuté.</strong> Un kipping pull-up où on tire avec la tête en avant et le menton qui sort, c'est 30 répétitions de stress cervical par série.</p>

      <h2>Les douleurs cervicales les plus fréquentes</h2>
      <h3>La cervicalgie commune (tension)</h3>
      <p>Douleur diffuse de la nuque + trapèzes supérieurs, parfois irradiation vers la base du crâne (céphalées de tension). Aggravée par la fatigue, le stress, la position assise prolongée. Excellente réponse à la mobilité thoracique + renforcement spécifique + adaptation temporaire de l'overhead.</p>

      <h3>La névralgie cervico-brachiale (NCB)</h3>
      <p>Douleur cervicale qui irradie dans le bras (épaule, coude, voire main), souvent dans un territoire précis (C5, C6, C7). Souvent liée à une hernie discale ou une arthrose foraminale qui irrite une racine nerveuse. <strong>Avis médical impératif</strong> avant de poursuivre overhead et kipping. La pratique adaptée du sport (cardio, bas du corps, gainage) reste possible et bénéfique.</p>

      <h3>L'arthrose cervicale (cervicarthrose)</h3>
      <p>Très fréquente après 50 ans, souvent asymptomatique. Quand elle est symptomatique : raideur matinale, douleurs rotatoires, parfois vertiges positionnels. Le sport adapté ralentit l'évolution. À éviter : les chocs verticaux directs (heavy box jumps, sprints sur sol dur) en phase aiguë.</p>

      <h3>Le torticolis aigu</h3>
      <p>Blocage musculaire brutal, souvent au réveil. Très douloureux, mais bénin. Mobilité douce, chaleur, pas d'overhead lourd pendant 5-7 jours. Récupération spontanée en 1-3 semaines.</p>

      <QuandConsulter>
        <p>Avis médical sans délai si :</p>
        <ul>
          <li>Douleur après un traumatisme (chute, choc, accident)</li>
          <li>Irradiation dans le bras avec fourmillements ou perte de force</li>
          <li>Maux de tête inhabituels et intenses</li>
          <li>Vertiges, troubles de l'équilibre, troubles visuels</li>
          <li>Fièvre associée à une raideur de nuque</li>
          <li>Douleur nocturne intense qui ne cède pas</li>
          <li>Douleur qui s'aggrave malgré 3-4 semaines de réduction d'activité</li>
        </ul>
      </QuandConsulter>

      <h2>Adapter ses séances quand la nuque tire</h2>
      <AdaptationTable rows={adaptations} />

      <h2>Le programme à intégrer</h2>
      <p><strong>Mobilité thoracique</strong> : foam roller thoracique, thoracic extensions sur foam, cat-cow, world's greatest stretch. 5-10 minutes tous les jours.</p>
      <p><strong>Renforcement des fléchisseurs profonds du cou</strong> : chin tucks (rentrer le menton sans hocher) en position allongée puis assise. 3 séries de 10 maintiens de 5 secondes, tous les jours.</p>
      <p><strong>Renforcement des stabilisateurs scapulaires</strong> : face pulls, band pull-aparts, Y-T-W, scapular pull-ups. 3 séries de 12-15, 3 fois par semaine.</p>
      <p><strong>Étirements ciblés</strong> : trapèze supérieur, élévateur de la scapula, sterno-cléido-mastoïdien. 30 secondes par côté, 2 fois par jour.</p>

      <AdaptationChistera>
        <p>Pour un adhérent avec douleur cervicale :</p>
        <ol>
          <li>Le coach interroge sur le contexte (post-trauma, posture pro, stress)</li>
          <li>Il vérifie le placement de la tête en wall balls, kipping, overhead</li>
          <li>Il adapte le volume overhead et substitue les mouvements problématiques</li>
          <li>Il intègre un mini-programme mobilité thoracique + chin tucks à pratiquer quotidiennement</li>
          <li>Si irradiation dans le bras, vertiges, ou douleur post-trauma : orientation médicale immédiate</li>
          <li>Si chronique non résolutif après 6 semaines : kiné spécialisé en thérapie manuelle cervicale</li>
        </ol>
      </AdaptationChistera>

      <FAQList items={faq.map((f) => ({ q: f.q, a: <p>{f.a}</p> }))} />

      <FurtherReading items={[
        { label: "Mal de dos et CrossFit", to: "/blog/mal-de-dos-sport-crossfit" },
        { label: "Douleur à l'épaule en CrossFit", to: "/blog/douleur-epaule-crossfit" },
        { label: "Sport après 35 ans : cadre actif", to: "/blog/sport-apres-35-ans-cadre-actif" },
        { label: "Faire du sport avec une douleur", to: "/blog/faire-du-sport-avec-douleur" },
      ]} />

      <SourcesSection items={[
        { label: "Côté et al. 2016 — Management of neck pain (Spine J)", href: "https://pubmed.ncbi.nlm.nih.gov/27282898/" },
        { label: "Gross et al. 2015 — Exercises for mechanical neck disorders (Cochrane)", href: "https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD004250.pub5/full" },
        { label: "Bier et al. 2018 — Clinical practice guideline for neck pain (Phys Ther)", href: "https://academic.oup.com/ptj/article/98/3/162/4924087" },
        { label: "WHO 2020 — Guidelines on physical activity and sedentary behaviour", href: "https://www.who.int/publications/i/item/9789240015128" },
      ]} />
    </ArticleLayout>
  );
}
